import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { Version, VersionFormData, Role } from '../models/version.model';
import { CHANGELOG_EDITOR_API_URI } from '../config';

type ApiVersion = {
  id: string;
  version?: { major: number; minor: number; patch: number };
  title?: string;
  role?: string;
  description?: string;
  language?: string;
  releaseDate?: string;
  createdBy?: string;
  createdAt?: string;
  changedBy?: string;
  changedAt?: string;
};

@Injectable({ providedIn: 'root' })
export class VersionService {
  private http = inject(HttpClient);
  private apiUri = inject(CHANGELOG_EDITOR_API_URI);

  private mapVersion(response: ApiVersion): Version {
    return {
      id: response.id?.toString(),
      major: response.version?.major ?? 0,
      minor: response.version?.minor ?? 0,
      patch: response.version?.patch ?? 0,
      title: response.title,
      role: response.role
        ? (response.role as string).split(',').map((r: string) => r.trim() as Role)
        : [],
      description: response.description ?? '',
      language: (response.language ?? 'en-US') as Version['language'],
      releaseDate: response.releaseDate ? new Date(response.releaseDate) : new Date(),
      createdBy: response.createdBy,
      createdAt: response.createdAt ? new Date(response.createdAt) : undefined,
      changedBy: response.changedBy,
      changedAt: response.changedAt ? new Date(response.changedAt) : undefined
    };
  }

  getAllVersions(): Observable<Version[]> {
    return this.http.post<ApiVersion[]>(`${this.apiUri}api/versioning/admin/query`, {}).pipe(
      map(responses => responses.map(r => this.mapVersion(r)))
    );
  }

  getVersionById(id: string): Observable<Version | undefined> {
    return this.http.get<ApiVersion>(`${this.apiUri}api/versioning/${id}`).pipe(
      map(response => this.mapVersion(response))
    );
  }

  addVersion(versionData: VersionFormData): Observable<Version> {
    const body = {
      version: {
        major: versionData.major,
        minor: versionData.minor,
        patch: versionData.patch ?? null
      },
      title: versionData.title ?? null,
      role: Array.isArray(versionData.role)
        ? versionData.role.join(',')
        : (versionData.role ?? null),
      description: versionData.description,
      releaseDate: versionData.releaseDate,
      language: versionData.language
    };

    return this.http.post<void>(`${this.apiUri}api/versioning/new`, body).pipe(
      map(() => ({
        major: versionData.major,
        minor: versionData.minor,
        patch: versionData.patch,
        title: versionData.title,
        role: versionData.role,
        description: versionData.description,
        language: versionData.language,
        releaseDate: versionData.releaseDate
      }))
    );
  }

  updateVersion(id: string, versionData: VersionFormData): Observable<Version> {
    const body = {
      id,
      version: {
        major: versionData.major,
        minor: versionData.minor,
        patch: versionData.patch ?? null
      },
      title: versionData.title ?? null,
      role: Array.isArray(versionData.role)
        ? versionData.role.join(',')
        : (versionData.role ?? null),
      description: versionData.description,
      releaseDate: versionData.releaseDate,
      language: versionData.language
    };

    return this.http.put<void>(`${this.apiUri}api/versioning/update`, body).pipe(
      map(() => ({
        id,
        major: versionData.major,
        minor: versionData.minor,
        patch: versionData.patch,
        title: versionData.title,
        role: versionData.role,
        description: versionData.description,
        language: versionData.language,
        releaseDate: versionData.releaseDate
      }))
    );
  }

  deleteVersion(id: string): Observable<boolean> {
    return this.http.delete<void>(`${this.apiUri}api/versioning/${id}`).pipe(
      map(() => true)
    );
  }
}
